import { useState, useRef, useEffect } from 'react';
import { convertImageUrl } from './config';
import AddressMap from './AddressMap';

interface Property {
  id: number;
  status?: string;
  space: {
    road_address: string;
    jibun_address?: string;
    floor: string;
    detail_address?: string;
    building_type: string;
    room_count: number;
    bathroom_count: number;
    kitchen_count: number;
    living_room_count: number;
    has_duplex: boolean;
    exclusive_area: number;
    has_elevator: boolean;
    parking_available: boolean;
    parking_option?: string;
    parking_info?: string;
  } | null;
  rent: {
    weekly_rent: number;
    deposit: number;
    weekly_maintenance_fee: number;
    maintenance_includes_electricity: boolean;
    maintenance_includes_water: boolean;
    maintenance_includes_gas: boolean;
    maintenance_detail?: string;
    checkout_cleaning_fee: number;
    cancellation_penalty: string;
    refund_policy_note: boolean;
  } | null;
  descriptions: {
    room_name: string;
    room_introduction: string;
    usage_guide?: string;
    transportation_info: string;
    additional_info?: string;
  } | null;
  photos: Array<{
    photo_url: string;
    display_order: number;
  }>;
  options: {
    basic_options: string[];
    additional_options: string[];
    pets_allowed: boolean;
  } | null;
  schedule: {
    min_contract_weeks: number;
    available_from?: string;
    available_until?: string;
  } | null;
  long_term_discounts: Array<{
    weeks: number;
    discount_rate: number;
  }>;
  immediate_discounts: Array<{
    days: number;
    discount_amount: number;
  }>;
}

interface PropertyDetailProps {
  property: Property;
  onBack: () => void;
  onEdit: (propertyId: number) => void;
  onRequestReview: (propertyId: number) => void;
  // 심사 모드 props
  reviewMode?: boolean;
  onApprove?: (propertyId: number) => void;
  onReject?: (propertyId: number) => void;
  onAiReview?: (propertyId: number) => void;
  aiReviewing?: boolean;
  // 어드민 모드 - 우측 가격/계약 패널 숨김
  adminMode?: boolean;
}

const OPTION_ICONS: Record<string, string> = {
  '냉장고': '🧊', '세탁기': '🫧', '에어컨': '❄️', 'TV': '📺',
  '와이파이': '📶', '싱크대': '🚰', '인덕션': '🔥', '침대': '🛏️',
  '전자레인지': '📡', '책상': '🪑', '소파': '🛋️', '도어락': '🔐',
  'CCTV': '📹', '관리실': '🏢', '식탁': '🍽️', '전기밥솥': '🍚',
  '옷장': '🗄️', '인터넷': '🌐', '헬스장': '💪', '수영장': '🏊',
  '택배보관함': '📦', '건조기': '💨', '오븐': '🍞', '식기세척기': '🍽️',
};

const formatPrice = (price: number | undefined) => {
  if (price === undefined || price === null) return '0';
  return price.toLocaleString('ko-KR');
};

const PropertyDetail = ({ property, onBack, onEdit, onRequestReview, reviewMode, onApprove, onReject, onAiReview, aiReviewing, adminMode }: PropertyDetailProps) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<'info' | 'contract'>('info');
  const [showMoreDescription, setShowMoreDescription] = useState(false);
  const [showDiscountDropdown, setShowDiscountDropdown] = useState(false);
  const [isDescriptionOverflow, setIsDescriptionOverflow] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const contractRef = useRef<HTMLDivElement>(null);

  const weeklyRent = property.rent?.weekly_rent || 0;
  const maintenanceFee = property.rent?.weekly_maintenance_fee || 0;
  const totalWeekly = weeklyRent + maintenanceFee;

  useEffect(() => {
    setCurrentPhotoIndex(0);
  }, [property.id]);

  useEffect(() => {
    if (descriptionRef.current) {
      setIsDescriptionOverflow(descriptionRef.current.scrollHeight > 120);
    }
  }, [property.descriptions?.additional_info]);

  // spin 애니메이션 keyframe 주입
  useEffect(() => {
    const styleId = 'spin-keyframe';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
      document.head.appendChild(style);
    }
  }, []);

  const scrollToContract = () => {
    setActiveTab('contract');
    contractRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div style={{ textAlign: 'left' }}>
      {/* 뒤로가기 */}
      <button
        onClick={onBack}
        style={{
          marginBottom: '20px', padding: '8px 0', border: 'none',
          background: 'none', fontSize: '15px', color: '#666',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = '#000'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = '#666'; }}
      >
        ← 목록으로 돌아가기
      </button>

      {/* 2단 레이아웃 */}
      <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start' }}>

        {/* ========== 좌측 콘텐츠 (65%) ========== */}
        <div style={{ flex: '1 1 65%', minWidth: 0 }}>

          {/* 사진 갤러리 */}
          <div style={{ marginBottom: '24px' }}>
            {property.photos && property.photos.length > 0 ? (
              <>
                <div style={{
                  position: 'relative', borderRadius: '12px', overflow: 'hidden',
                  backgroundColor: '#f0f0f0'
                }}>
                  <img
                    src={convertImageUrl(property.photos[currentPhotoIndex].photo_url)}
                    alt={`${property.descriptions?.room_name || '매물'} ${currentPhotoIndex + 1}`}
                    style={{ width: '100%', height: '450px', objectFit: 'cover', display: 'block' }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <div style={{
                    position: 'absolute', bottom: '16px', right: '16px',
                    backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff',
                    padding: '6px 14px', borderRadius: '20px', fontSize: '14px', fontWeight: 'bold'
                  }}>
                    {currentPhotoIndex + 1} / {property.photos.length}
                  </div>
                  {property.photos.length > 1 && currentPhotoIndex > 0 && (
                    <button onClick={() => setCurrentPhotoIndex(currentPhotoIndex - 1)} style={{
                      position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)',
                      width: '40px', height: '40px', borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.9)', border: 'none', cursor: 'pointer',
                      fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}>←</button>
                  )}
                  {property.photos.length > 1 && currentPhotoIndex < property.photos.length - 1 && (
                    <button onClick={() => setCurrentPhotoIndex(currentPhotoIndex + 1)} style={{
                      position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)',
                      width: '40px', height: '40px', borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.9)', border: 'none', cursor: 'pointer',
                      fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}>→</button>
                  )}
                </div>
                {property.photos.length > 1 && (
                  <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', padding: '12px 0' }}>
                    {property.photos.map((photo, i) => (
                      <div key={i} onClick={() => setCurrentPhotoIndex(i)} style={{
                        minWidth: '80px', height: '60px', borderRadius: '6px', overflow: 'hidden',
                        cursor: 'pointer',
                        border: currentPhotoIndex === i ? '2px solid #6B4EFF' : '2px solid transparent',
                        opacity: currentPhotoIndex === i ? 1 : 0.5, transition: 'all 0.2s'
                      }}>
                        <img src={convertImageUrl(photo.photo_url)} alt={`썸네일 ${i + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div style={{
                width: '100%', height: '300px', backgroundColor: '#f5f5f5', borderRadius: '12px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '15px'
              }}>
                등록된 사진이 없습니다
              </div>
            )}
          </div>

          {/* 제목 + 주소 */}
          <h1 style={{ margin: '0 0 6px 0', fontSize: '26px', fontWeight: 'bold', color: '#000' }}>
            {property.descriptions?.room_name || '매물 이름'}
          </h1>
          <p style={{ margin: '0 0 24px 0', fontSize: '15px', color: '#666' }}>
            {property.space?.road_address}{property.space?.floor ? ` ${property.space.floor}` : ''}
          </p>

          {/* 탭 네비게이션 */}
          <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid #eee', marginBottom: '30px' }}>
            <button onClick={() => setActiveTab('info')} style={{
              padding: '14px 24px', border: 'none', background: 'none', fontSize: '15px', cursor: 'pointer',
              fontWeight: activeTab === 'info' ? 'bold' : 'normal',
              color: activeTab === 'info' ? '#000' : '#999',
              borderBottom: activeTab === 'info' ? '2px solid #000' : '2px solid transparent',
              marginBottom: '-1px'
            }}>방 정보</button>
            <button onClick={scrollToContract} style={{
              padding: '14px 24px', border: 'none', background: 'none', fontSize: '15px', cursor: 'pointer',
              fontWeight: activeTab === 'contract' ? 'bold' : 'normal',
              color: activeTab === 'contract' ? '#000' : '#999',
              borderBottom: activeTab === 'contract' ? '2px solid #000' : '2px solid transparent',
              marginBottom: '-1px'
            }}>계약 정보</button>
          </div>

          {/* ===== 방 정보 탭 콘텐츠 ===== */}

          {/* 구조 + 건물유형 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '30px' }}>
            <div>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: 'bold' }}>구조</h3>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {[
                  { icon: '🛏️', label: '방', value: `${property.space?.room_count || 0}개` },
                  { icon: '🚿', label: '화장실', value: `${property.space?.bathroom_count || 0}개` },
                  { icon: '🍳', label: '주방', value: `${property.space?.kitchen_count || 0}개` },
                  { icon: '🛋️', label: '거실', value: `${property.space?.living_room_count || 0}개` },
                ].map((item, i, arr) => (
                  <div key={item.label} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 0',
                    borderBottom: i < arr.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#333', fontSize: '15px' }}>
                      <span style={{ fontSize: '18px', width: '24px' }}>{item.icon}</span>
                      {item.label}
                    </div>
                    <span style={{ fontSize: '15px', color: '#333' }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: 'bold' }}>건물 유형 및 면적</h3>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 0', borderBottom: '1px solid #f0f0f0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#333', fontSize: '15px' }}>
                    <span style={{ fontSize: '18px', width: '24px' }}>🏢</span>
                    건물 유형
                  </div>
                  <span style={{ fontSize: '15px', color: '#333' }}>{property.space?.building_type || '-'}</span>
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#333', fontSize: '15px' }}>
                    <span style={{ fontSize: '18px', width: '24px' }}>📐</span>
                    전용 면적
                  </div>
                  <span style={{ fontSize: '15px', color: '#333' }}>
                    {property.space?.exclusive_area
                      ? `${property.space.exclusive_area}평 (${Math.round(property.space.exclusive_area * 3.3058)}m²)`
                      : '면적 정보 없음'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 복층 안내 */}
          {property.space?.has_duplex && (
            <div style={{
              backgroundColor: '#F3F0FF', borderRadius: '8px', padding: '16px', marginBottom: '30px',
              fontSize: '14px', color: '#5940CC', lineHeight: '1.5'
            }}>
              <strong>복층 구조의 집</strong>으로 기재된 전용 면적보다 실제 사용 면적이 더 넓습니다.
            </div>
          )}

          {/* 엘리베이터 및 주차 */}
          <div style={{ borderTop: '1px solid #eee', paddingTop: '30px', marginBottom: '30px' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: 'bold' }}>엘리베이터 및 주차</h3>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 0', borderBottom: '1px solid #f0f0f0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#333', fontSize: '15px' }}>
                  <span style={{ fontSize: '18px', width: '24px' }}>🔼</span>
                  엘리베이터
                </div>
                <span style={{ fontSize: '15px', color: '#333' }}>
                  {property.space?.has_elevator ? '있음' : '없음'}
                </span>
              </div>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#333', fontSize: '15px' }}>
                  <span style={{ fontSize: '18px', width: '24px' }}>🅿️</span>
                  주차
                </div>
                <span style={{ fontSize: '15px', color: '#333' }}>
                  {property.space?.parking_available
                    ? (property.space.parking_option || '가능')
                    : '주차 불가'}
                </span>
              </div>
            </div>
            {property.space?.parking_available && property.space?.parking_info && (
              <div style={{
                backgroundColor: '#F8F8F8', borderRadius: '8px', padding: '16px', marginTop: '16px'
              }}>
                <p style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: 'bold', color: '#333' }}>주차 안내</p>
                <p style={{ margin: 0, fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                  {property.space.parking_info}
                </p>
              </div>
            )}
          </div>

          {/* 방 소개 */}
          {property.descriptions?.room_introduction && (
            <div style={{ borderTop: '1px solid #eee', paddingTop: '30px', marginTop: '10px', marginBottom: '30px' }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: 'bold' }}>방 소개</h3>
              <p style={{
                margin: 0, whiteSpace: 'pre-line', lineHeight: '1.8', color: '#333', fontSize: '15px',
                textAlign: 'left'
              }}>
                {property.descriptions.room_introduction}
              </p>
            </div>
          )}

          {/* 위치 (지도) */}
          {property.space?.road_address && (
            <div style={{ borderTop: '1px solid #eee', paddingTop: '30px', marginBottom: '30px' }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: 'bold' }}>위치</h3>
              <p style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#333', textAlign: 'left' }}>
                {property.space.road_address}{property.space.floor ? ` ${property.space.floor}` : ''}
              </p>
              <AddressMap address={property.space.road_address} />
            </div>
          )}

          {/* 교통 */}
          {property.descriptions?.transportation_info && (
            <div style={{ borderTop: '1px solid #eee', paddingTop: '30px', marginBottom: '30px' }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: 'bold' }}>교통</h3>
              <p style={{
                margin: 0, whiteSpace: 'pre-line', lineHeight: '1.8', color: '#333', fontSize: '15px',
                textAlign: 'left'
              }}>
                {property.descriptions.transportation_info}
              </p>
            </div>
          )}

          {/* 기본 옵션 */}
          {property.options?.basic_options && property.options.basic_options.length > 0 && (
            <div style={{ borderTop: '1px solid #eee', paddingTop: '30px', marginBottom: '30px' }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: 'bold' }}>기본 옵션</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
                {property.options.basic_options.map((option) => (
                  <div key={option} style={{
                    width: '72px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px'
                  }}>
                    <span style={{ fontSize: '24px' }}>{OPTION_ICONS[option] || '📦'}</span>
                    <span style={{ fontSize: '12px', color: '#555', textAlign: 'center', lineHeight: '1.3' }}>{option}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 추가 옵션 */}
          {property.options?.additional_options && property.options.additional_options.length > 0 && (
            <div style={{ borderTop: '1px solid #eee', paddingTop: '30px', marginBottom: '30px' }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: 'bold' }}>추가 옵션</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
                {property.options.additional_options.map((option) => (
                  <div key={option} style={{
                    width: '72px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px'
                  }}>
                    <span style={{ fontSize: '24px' }}>{OPTION_ICONS[option] || '📦'}</span>
                    <span style={{ fontSize: '12px', color: '#555', textAlign: 'center', lineHeight: '1.3' }}>{option}</span>
                  </div>
                ))}
              </div>
              {property.options.pets_allowed && (
                <div style={{
                  marginTop: '20px', display: 'inline-block',
                  padding: '8px 16px', backgroundColor: '#E8F5E9', borderRadius: '20px',
                  fontSize: '14px', color: '#2E7D32', fontWeight: 'bold'
                }}>
                  🐾 반려동물 동반 가능
                </div>
              )}
            </div>
          )}

          {/* 반려동물 (추가옵션이 없지만 pets_allowed인 경우) */}
          {property.options?.pets_allowed &&
            (!property.options?.additional_options || property.options.additional_options.length === 0) && (
            <div style={{ borderTop: '1px solid #eee', paddingTop: '30px', marginBottom: '30px' }}>
              <div style={{
                display: 'inline-block', padding: '8px 16px', backgroundColor: '#E8F5E9',
                borderRadius: '20px', fontSize: '14px', color: '#2E7D32', fontWeight: 'bold'
              }}>
                🐾 반려동물 동반 가능
              </div>
            </div>
          )}

          {/* 방 이용 방법 */}
          {property.descriptions?.usage_guide && (
            <div style={{ borderTop: '1px solid #eee', paddingTop: '30px', marginBottom: '30px' }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: 'bold' }}>방 이용 방법</h3>
              <p style={{
                margin: 0, whiteSpace: 'pre-line', lineHeight: '1.8', color: '#333', fontSize: '15px',
                textAlign: 'left'
              }}>
                {property.descriptions.usage_guide}
              </p>
            </div>
          )}

          {/* 추가 설명 */}
          {property.descriptions?.additional_info && (
            <div style={{ borderTop: '1px solid #eee', paddingTop: '30px', marginBottom: '30px' }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: 'bold' }}>추가 설명</h3>
              <div
                ref={descriptionRef}
                style={{
                  maxHeight: showMoreDescription ? 'none' : '120px',
                  overflow: 'hidden', transition: 'max-height 0.3s ease',
                  whiteSpace: 'pre-line', lineHeight: '1.8', color: '#333', fontSize: '15px',
                  textAlign: 'left'
                }}
              >
                {property.descriptions.additional_info}
              </div>
              {isDescriptionOverflow && (
                <button
                  onClick={() => setShowMoreDescription(!showMoreDescription)}
                  style={{
                    marginTop: '12px', padding: '0', border: 'none', background: 'none',
                    fontSize: '14px', color: '#666', cursor: 'pointer', display: 'flex',
                    alignItems: 'center', gap: '4px'
                  }}
                >
                  {showMoreDescription ? '접기 ∧' : '더보기 ∨'}
                </button>
              )}
            </div>
          )}

          {/* 관리비 포함 항목 + 관리비 안내 */}
          <div style={{ borderTop: '1px solid #eee', paddingTop: '30px', marginBottom: '30px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
              <div>
                <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: 'bold' }}>관리비 포함 항목</h3>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {[
                    { icon: '🔥', label: '가스', included: property.rent?.maintenance_includes_gas },
                    { icon: '💧', label: '수도', included: property.rent?.maintenance_includes_water },
                    { icon: '⚡', label: '전기', included: property.rent?.maintenance_includes_electricity },
                  ].map((item, i) => (
                    <div key={item.label} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '14px 0',
                      borderBottom: i < 2 ? '1px solid #f0f0f0' : 'none'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '15px', color: '#333' }}>
                        <span style={{ fontSize: '18px' }}>{item.icon}</span>
                        {item.label}
                      </div>
                      <span style={{ fontSize: '15px', color: item.included ? '#333' : '#ccc' }}>
                        {item.included ? '포함' : '미포함'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              {property.rent?.maintenance_detail && (
                <div>
                  <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: 'bold' }}>관리비 안내</h3>
                  <p style={{ margin: 0, fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                    {property.rent.maintenance_detail}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* 계약 정보 카드 */}
          <div ref={contractRef} style={{
            backgroundColor: '#F8F8F8', borderRadius: '12px', padding: '28px', marginBottom: '30px'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: 'bold' }}>계약 정보</h3>

            {/* 임대료 */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 0', borderBottom: '1px solid #e8e8e8'
            }}>
              <span style={{ fontSize: '15px', color: '#555' }}>임대료</span>
              <span style={{ fontSize: '15px', fontWeight: '600', color: '#222' }}>{formatPrice(weeklyRent)}원 / 1주</span>
            </div>

            {/* 장기 계약 할인 */}
            {property.long_term_discounts && property.long_term_discounts.length > 0 && (
              <div style={{
                backgroundColor: '#fff', borderRadius: '10px', padding: '16px 18px',
                margin: '12px 0', border: '1px solid #eee'
              }}>
                <p style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600', color: '#444' }}>장기 계약 할인</p>
                {property.long_term_discounts.map((d, i) => {
                  const discountedPrice = Math.round(weeklyRent * (1 - d.discount_rate / 100));
                  return (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '4px 0', marginTop: i > 0 ? '4px' : '0'
                    }}>
                      <span style={{ fontSize: '13px', color: '#777' }}>
                        {d.weeks}주 이상 계약 시 {d.discount_rate}% 할인
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#6B4EFF' }}>
                        {formatPrice(discountedPrice)}원 / 1주
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* 즉시 입주 할인 */}
            {property.immediate_discounts && property.immediate_discounts.length > 0 && (
              <div style={{
                backgroundColor: '#fff', borderRadius: '10px', padding: '16px 18px',
                margin: '12px 0', border: '1px solid #eee'
              }}>
                <p style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600', color: '#444' }}>즉시 입주 할인</p>
                {property.immediate_discounts.map((d, i) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '4px 0', marginTop: i > 0 ? '4px' : '0'
                  }}>
                    <span style={{ fontSize: '13px', color: '#777' }}>
                      {d.days}일 내 입주 시
                    </span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#2E7D32' }}>
                      {formatPrice(d.discount_amount)}원 할인
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* 관리비 */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 0', borderBottom: '1px solid #e8e8e8'
            }}>
              <span style={{ fontSize: '15px', color: '#555' }}>관리비</span>
              <span style={{ fontSize: '15px', fontWeight: '600', color: '#222' }}>{formatPrice(maintenanceFee)}원 / 1주</span>
            </div>

            {/* 청소비 */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 0', borderBottom: '1px solid #e8e8e8'
            }}>
              <span style={{ fontSize: '15px', color: '#555' }}>청소비</span>
              <span style={{ fontSize: '15px', fontWeight: '600', color: '#222' }}>
                {formatPrice(property.rent?.checkout_cleaning_fee || 0)}원
              </span>
            </div>

            {/* 보증금 */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 0'
            }}>
              <span style={{ fontSize: '15px', color: '#555' }}>보증금</span>
              <span style={{ fontSize: '15px', fontWeight: '600', color: '#222' }}>
                {formatPrice(property.rent?.deposit || 0)}원
              </span>
            </div>

            {/* 보증금 안내 */}
            <p style={{
              margin: '8px 0 0 0', fontSize: '13px', color: '#999',
              paddingTop: '12px', borderTop: '1px solid #e8e8e8'
            }}>
              ⓘ 보증금은 퇴실 후 반환됩니다.
            </p>
          </div>

        </div>

        {/* ========== 우측 사이드바 (35%) ========== */}
        {!adminMode && <div style={{
          flex: '0 0 340px', position: 'sticky', top: '80px',
          backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '12px',
          padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
        }}>
          {/* 최소 계약 기간 */}
          <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666' }}>
            최소 계약 기간 {property.schedule?.min_contract_weeks || 1}주
          </p>

          {/* 총 금액 */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '6px' }}>
            <span style={{ fontSize: '32px', fontWeight: 'bold', color: '#000' }}>
              {formatPrice(totalWeekly)}원
            </span>
            <span style={{ fontSize: '16px', color: '#6B4EFF', fontWeight: 'bold' }}>/ 1주</span>
          </div>

          {/* 임대료 + 관리비 */}
          <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#666' }}>
            임대료 {formatPrice(weeklyRent)}원 &nbsp; 관리비 {formatPrice(maintenanceFee)}원
          </p>

          <div style={{ borderTop: '1px solid #eee', margin: '0 0 16px 0' }} />

          {/* 할인 정보 */}
          {(property.long_term_discounts?.length > 0 || property.immediate_discounts?.length > 0) && (
            <>
              <div
                onClick={() => setShowDiscountDropdown(!showDiscountDropdown)}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  cursor: 'pointer', marginBottom: '4px'
                }}
              >
                <span style={{ fontSize: '14px', color: '#666' }}>할인</span>
                <span style={{ fontSize: '14px', color: '#6B4EFF', fontWeight: 'bold' }}>
                  {property.long_term_discounts?.length > 0 &&
                    `장기계약 -${property.long_term_discounts[0].discount_rate}%`}
                  {property.long_term_discounts?.length > 0 && property.immediate_discounts?.length > 0 && ' / '}
                  {property.immediate_discounts?.length > 0 && '즉시입주 할인'}
                  &nbsp;{showDiscountDropdown ? '∧' : '∨'}
                </span>
              </div>

              {showDiscountDropdown && (
                <div style={{
                  backgroundColor: '#F8F8F8', borderRadius: '8px', padding: '14px',
                  marginBottom: '12px', marginTop: '8px'
                }}>
                  {property.long_term_discounts?.length > 0 && (
                    <div style={{ marginBottom: property.immediate_discounts?.length > 0 ? '12px' : '0' }}>
                      <p style={{ margin: '0 0 6px 0', fontSize: '13px', fontWeight: 'bold', color: '#333' }}>장기 계약 할인</p>
                      {property.long_term_discounts.map((d, i) => (
                        <p key={i} style={{ margin: '2px 0', fontSize: '13px', color: '#666' }}>
                          · {d.weeks}주 이상 계약 시 {d.discount_rate}% 할인
                        </p>
                      ))}
                    </div>
                  )}
                  {property.immediate_discounts?.length > 0 && (
                    <div>
                      <p style={{ margin: '0 0 6px 0', fontSize: '13px', fontWeight: 'bold', color: '#333' }}>즉시 입주 할인</p>
                      {property.immediate_discounts.map((d, i) => (
                        <p key={i} style={{ margin: '2px 0', fontSize: '13px', color: '#666' }}>
                          · {d.days}일 내 입주 시 {formatPrice(d.discount_amount)}원 할인
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div style={{ borderTop: '1px solid #eee', margin: '12px 0 16px 0' }} />
            </>
          )}

          {reviewMode ? (
            <>
              {/* 심사 모드: 심사중 상태 표시 */}
              <div style={{
                padding: '10px 14px', backgroundColor: '#FFF3E0', borderRadius: '8px',
                marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px'
              }}>
                <span style={{
                  backgroundColor: '#FF6B6B', color: '#fff', padding: '3px 10px',
                  borderRadius: '4px', fontSize: '12px', fontWeight: 'bold'
                }}>심사중</span>
                <span style={{ fontSize: '13px', color: '#666' }}>심사 대기 중인 매물입니다</span>
              </div>

              {/* AI 자동 심사 */}
              {onAiReview && (
                <button
                  onClick={() => !aiReviewing && onAiReview(property.id)}
                  disabled={aiReviewing}
                  style={{
                    width: '100%', padding: '14px',
                    backgroundColor: aiReviewing ? '#CE93D8' : '#9C27B0',
                    color: '#fff',
                    border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 'bold',
                    cursor: aiReviewing ? 'not-allowed' : 'pointer',
                    marginBottom: '8px', transition: 'background-color 0.2s',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                  }}
                  onMouseEnter={(e) => { if (!aiReviewing) e.currentTarget.style.backgroundColor = '#7B1FA2'; }}
                  onMouseLeave={(e) => { if (!aiReviewing) e.currentTarget.style.backgroundColor = '#9C27B0'; }}
                >
                  {aiReviewing ? (
                    <>
                      <span style={{
                        display: 'inline-block', width: '16px', height: '16px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTopColor: '#fff', borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite'
                      }} />
                      AI 심사 중...
                    </>
                  ) : (
                    <><span>🤖</span> AI 자동 심사</>
                  )}
                </button>
              )}
              {aiReviewing && (
                <p style={{
                  margin: '0 0 8px 0', fontSize: '13px', color: '#9C27B0',
                  textAlign: 'center'
                }}>
                  AI가 매물을 분석하고 있습니다. 잠시만 기다려주세요...
                </p>
              )}

              {/* 승인 / 거절 */}
              <div style={{ display: 'flex', gap: '8px' }}>
                {onApprove && (
                  <button
                    onClick={() => {
                      if (window.confirm('이 매물을 승인하시겠습니까?')) {
                        onApprove(property.id);
                      }
                    }}
                    style={{
                      flex: 1, padding: '14px', backgroundColor: '#4CAF50', color: '#fff',
                      border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 'bold',
                      cursor: 'pointer', transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#45A049'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#4CAF50'; }}
                  >
                    ✓ 승인
                  </button>
                )}
                {onReject && (
                  <button
                    onClick={() => {
                      if (window.confirm('이 매물을 거절하시겠습니까?')) {
                        onReject(property.id);
                      }
                    }}
                    style={{
                      flex: 1, padding: '14px', backgroundColor: '#F44336', color: '#fff',
                      border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 'bold',
                      cursor: 'pointer', transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#D32F2F'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#F44336'; }}
                  >
                    ✗ 거절
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              {/* 일반 모드: 계약/심사요청/수정 */}
              <button
                onClick={() => alert('계약 기능은 준비 중입니다.')}
                style={{
                  width: '100%', padding: '16px', backgroundColor: '#333', color: '#fff',
                  border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold',
                  cursor: 'pointer', marginBottom: '12px', transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#555'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#333'; }}
              >
                계약 시작하기
              </button>

              {property.status === 'active' && (
                <button
                  onClick={() => {
                    if (window.confirm('이 매물을 심사 요청하시겠습니까?')) {
                      onRequestReview(property.id);
                    }
                  }}
                  style={{
                    width: '100%', padding: '14px', backgroundColor: '#6B4EFF', color: '#fff',
                    border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 'bold',
                    cursor: 'pointer', marginBottom: '8px', transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#5940CC'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#6B4EFF'; }}
                >
                  심사 요청하기
                </button>
              )}

              <button
                onClick={() => onEdit(property.id)}
                style={{
                  width: '100%', padding: '14px', backgroundColor: '#fff', color: '#333',
                  border: '1px solid #ddd', borderRadius: '8px', fontSize: '15px', fontWeight: 'bold',
                  cursor: 'pointer', transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
              >
                수정하기
              </button>
            </>
          )}
        </div>}

      </div>
    </div>
  );
};

export default PropertyDetail;
